/*
 * Silk Romance - Spin & Win
 *
 * Version history (v1-v16: wheel, prizes, discount-code flow, CRM relay, App Proxy, unique codes, reopen fix) lives in
 * the theme's version history / earlier DRAFT themes; the long per-version essay that used to sit here was trimmed on
 * 2026-10-04 to keep this file readable. Behaviour notes that still matter:
 *  - PRIZES / weights / codeForPrize() are the business rules - do not change without the owner.
 *  - Free Shipping is NOT in the winnable pool (paused); the wheel still shows the slice, it just never lands there.
 *  - Apply Offer clears the cart discount then applies the won code via /cart/update.js and stays on the same page
 *    (when the cart has items - see v20 for the empty-cart case).
 *  - Auto-open is intent-based (scroll >= 30%, mobile 3s after first touch, desktop exit-intent), once per session.
 *  - The CRM relay goes through the Shopify App Proxy (/apps/sr/spin-wheel-lead.php); no secret lives in this file.
 *
 * v17 (2026-10-04): getBridgedVisitorId() reads the CRM pixel's own visitor id from this page's localStorage
 *   ('tbf_ce_visitor_id') FIRST and only falls back to the cart attribute (520 of 824 spin submissions had no visitor id).
 *
 * v18 (2026-10-04): (a) INTERNATIONAL numbers via the switch sr-capture-pack.js adds (window.SR_SPIN_INTL); (b) attempt()
 *   re-checks the once-per-session key so a popup sr-capture-pack.js already opened for a high-intent arrival is not opened twice.
 *
 * v19 (2026-10-04): auto-open was being swallowed by the attention arbiter's 24h cooldown and by a single 4s retry. attempt() now
 *   drops OUR OWN 'spin_auto' cooldown entry before asking and keeps retrying every 3s for ~45s while another interaction holds
 *   the slot.
 *
 * v20 (2026-10-04): EMPTY CART + "Apply Offer": confirm, close the popup and go straight to /collections/all instead of leaving a
 *   disabled "Code Saved" button and a second "Continue Shopping" tap. With items in the cart nothing changes.
 *
 * v21 (2026-10-04, welcome points): the relay now also sends the optional EMAIL typed into the field sr-capture-pack.js adds next to
 *   Name (api/spin-wheel-lead.php already validates and stores it). A Shopify login needs an email, and the 200 Gift Points the CRM
 *   credits to every new customer can only be used by a logged-in customer - so without an email they were unusable. Nothing else changed.
 *
 * v22 (2026-10-08, spin rules by order history): the lead-capture call now WAITS for the server's `eligibility` answer before the
 *   wheel is shown (api/spin-wheel-lead.php, includes/spin-wheel-guard-helper.php):
 *     - mode 'blocked': the person already used their spin recently -> the server's message is shown, no spin.
 *     - mode 'reuse'  : they already hold a usable code -> that SAME code is shown (no spin, no new code).
 *     - mode 'spin'   : cap_percent (customers with past orders) limits which prizes the wheel can land on, so the wheel never shows a
 *                       prize the server would then downgrade. Server enforces the same cap on the prize call.
 *   An Indian mobile must be exactly 10 digits typed as digits (no spaces, no +91). If the server cannot be reached the old behaviour
 *   applies (the server still enforces the rules when the prize is claimed).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sr_spin_state_v1';
  var track = function (name, payload) {
    if (window.SREvents) window.SREvents.track(name, payload);
  };

  // --- CRM relay: api/spin-wheel-lead.php on the mastercrm ERP, via the shop's own Shopify App Proxy ---
  var RELAY_URL = '/apps/sr/spin-wheel-lead.php';

  function getBridgedVisitorId() {
    var local = '';
    try { local = window.localStorage.getItem('tbf_ce_visitor_id') || ''; } catch (e) {}
    if (local) return Promise.resolve(local);
    return fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return (cart && cart.attributes && cart.attributes._tbf_visitor_id) || ''; })
      .catch(function () { return ''; });
  }

  // The relay RESOLVES WITH the server's response so the caller can read the unique, single-use discount code the API
  // mints per winner. Always resolves, never rejects: a network failure, a non-JSON body or a slow server all resolve to
  // null, which the caller treats as "use the local fallback code".
  var RELAY_TIMEOUT_MS = 6000;

  function relaySubmitLead(payload) {
    if (!RELAY_URL) return Promise.resolve(null); // not configured - no-op
    return getBridgedVisitorId().then(function (visitorId) {
      var body = Object.assign({}, payload, { visitor_id: visitorId, is_international: !!window.SR_SPIN_INTL });
      var request = fetch(RELAY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        keepalive: true
      }).then(function (r) { return r.json(); }).catch(function () { return null; });

      var timeout = new Promise(function (resolve) {
        window.setTimeout(function () { resolve(null); }, RELAY_TIMEOUT_MS);
      });

      return Promise.race([request, timeout]);
    }).catch(function () { return null; });
  }

  // Wraps a promise so callers can ask "has this landed yet?" without awaiting it.
  function settleBox(promise) {
    var box = { settled: false, value: null };
    box.promise = promise.then(
      function (v) { box.settled = true; box.value = v; return v; },
      function () { box.settled = true; box.value = null; return null; }
    );
    return box;
  }

  // The API mints a unique single-use code per winner and returns it as `code`. If minting failed the server still
  // answers 200 with the code we sent, so this is safe to read either way. The adopted code is written back to saved
  // state so "Apply Offer" applies the same code the visitor was shown.
  function adoptServerCode(state, response) {
    var minted = response && typeof response.code === 'string' ? response.code.trim() : '';
    if (minted && minted !== state.code) {
      state.code = minted;
      writeState(state);
    }
    return state.code;
  }

  // PAUSED: Free Shipping removed from the winnable pool pending the business's threshold decision. Five percentage
  // tiers only, weights 40/30/20/9/1 = 100.
  var PRIZES = [
    { id: '5off', label: '5% OFF', weight: 40, type: 'percent', value: 5, segIndex: 0 },
    { id: '7_5off', label: '7.5% OFF', weight: 30, type: 'percent', value: 7.5, segIndex: 1 },
    { id: '10off', label: '10% OFF', weight: 20, type: 'percent', value: 10, segIndex: 2 },
    { id: '12off', label: '12% OFF', weight: 9, type: 'percent', value: 12, segIndex: 3 },
    { id: '15off', label: '15% OFF', weight: 1, type: 'percent', value: 15, segIndex: 4 }
    // 'freeship' (segIndex 5) intentionally NOT in this pool while paused.
  ];
  var TOTAL_SEGMENTS = 6; // wheel graphic still has 6 visual slices (FREE SHIP kept, non-landing)

  // v22: highest percentage this visitor may win, from the server's eligibility answer (null = no limit). The server compares the
  // NUMBER in the prize code (SRSPIN7 -> 7), so the same number is used here.
  var spinCap = null;
  function prizeCodeNumber(prize) {
    var m = /SRSPIN(\d+)/.exec(codeForPrize(prize) || '');
    return m ? parseFloat(m[1]) : 0;
  }

  function weightedPick() {
    var pool = PRIZES.filter(function (p) { return spinCap === null || prizeCodeNumber(p) <= spinCap; });
    if (!pool.length) pool = [PRIZES[0]];
    var total = pool.reduce(function (s, p) { return s + p.weight; }, 0);
    var r = Math.random() * total;
    var acc = 0;
    for (var i = 0; i < pool.length; i++) {
      acc += pool[i].weight;
      if (r <= acc) return { prize: pool[i], index: i };
    }
    return { prize: pool[pool.length - 1], index: pool.length - 1 };
  }

  // v22: the prize whose value is closest to an existing code's percentage (for showing a returning visitor's offer).
  function prizeForPercent(percent) {
    if (typeof percent !== 'number') return null;
    var best = null;
    PRIZES.forEach(function (p) {
      if (best === null || Math.abs(p.value - percent) < Math.abs(best.value - percent)) best = p;
    });
    return best && Math.abs(best.value - percent) <= 0.6 ? best : null;
  }

  function readState() {
    try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (e) { return null; }
  }
  function writeState(state) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  // v22: an Indian number is exactly 10 digits, typed as digits - NO spaces, +91 or dashes (it used to be stripped to digits first, so the
  // same person typed in different shapes). "Outside India" (set by sr-capture-pack.js) still accepts 7-15 digits including the country code.
  function validMobile(v) {
    var raw = (v || '').trim();
    if (window.SR_SPIN_INTL) {
      var digits = raw.replace(/\D/g, '');
      return /^\d{7,15}$/.test(digits) ? digits : null;
    }
    return /^[6-9]\d{9}$/.test(raw) ? raw : null;
  }

  // Real, merchant-verified Shopify discount codes. 'freeship' mapping is kept (SRSPINSHIP still exists) but is
  // unreachable while PRIZES omits 'freeship' above.
  var PRIZE_CODES = {
    '5off': 'SRSPIN5',
    '7_5off': 'SRSPIN7',
    '10off': 'SRSPIN10',
    '12off': 'SRSPIN12',
    '15off': 'SRSPIN15',
    'freeship': 'SRSPINSHIP'
  };
  function codeForPrize(prize) {
    return PRIZE_CODES[prize.id] || null;
  }

  // rAF-driven spin animation: 3-phase eased progress (short ease-in, fast near-linear cruise, longer ease-out).
  function spinEaseProgress(t) {
    if (t < 0.08) {
      var p1 = t / 0.08;
      return 0.02 * (p1 * p1);
    } else if (t < 0.65) {
      var p2 = (t - 0.08) / (0.65 - 0.08);
      return 0.02 + 0.68 * p2;
    } else {
      var p3 = (t - 0.65) / (1 - 0.65);
      return 0.70 + 0.30 * (1 - Math.pow(1 - p3, 3));
    }
  }

  function prefersReducedMotion() {
    try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (e) { return false; }
  }

  // Animates wheelEl's rotation from 0 to finalDeg over durationMs using requestAnimationFrame, then calls onDone().
  function spinWheelTo(wheelEl, finalDeg, durationMs, onDone) {
    if (prefersReducedMotion()) {
      wheelEl.style.transition = 'opacity 0.5s ease';
      wheelEl.style.opacity = '0.4';
      wheelEl.style.transform = 'rotate(' + finalDeg + 'deg)';
      window.setTimeout(function () {
        wheelEl.style.opacity = '1';
        onDone();
      }, 220);
      return;
    }
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / durationMs);
      var eased = spinEaseProgress(t);
      wheelEl.style.transform = 'rotate(' + (finalDeg * eased) + 'deg)';
      if (t < 1) {
        window.requestAnimationFrame(frame);
      } else {
        wheelEl.style.transform = 'rotate(' + finalDeg + 'deg)'; // guarantee exact, deterministic final angle
        onDone();
      }
    }
    window.requestAnimationFrame(frame);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.getElementById('sr-spin-win-root');
    if (!root) return;

    var launcher = root.querySelector('.sr-spin-launcher');
    var overlay = root.querySelector('.sr-spin-overlay');
    var closeBtn = root.querySelector('.sr-spin-close');
    var steps = {
      capture: root.querySelector('[data-sr-step="capture"]'),
      spin: root.querySelector('[data-sr-step="spin"]'),
      result: root.querySelector('[data-sr-step="result"]')
    };
    var mobileInput = root.querySelector('[data-sr-mobile]');
    var consentInput = root.querySelector('[data-sr-consent]');
    var nameInput = root.querySelector('[data-sr-name]');
    var captureErr = root.querySelector('[data-sr-capture-error]');
    var continueBtn = root.querySelector('[data-sr-continue]');
    var spinCta = root.querySelector('[data-sr-spin-cta]');
    var wheelSvg = root.querySelector('.sr-wheel-svg');
    var resultIcon = root.querySelector('[data-sr-result-icon]');
    var resultTitle = root.querySelector('[data-sr-result-title]');
    var resultDesc = root.querySelector('[data-sr-result-desc]');
    var couponCard = root.querySelector('[data-sr-coupon-card]');
    var couponCode = root.querySelector('[data-sr-coupon-code]');
    var couponMeta = root.querySelector('[data-sr-coupon-meta]');
    var applyBtn = root.querySelector('[data-sr-apply]');
    var confettiHost = root.querySelector('[data-sr-confetti]');

    var existing = readState();

    // v21: the email field is added by sr-capture-pack.js after load, so it is looked up when needed. Malformed -> null (the server
    // would drop it anyway; it must never block the spin).
    function spinEmail() {
      var el = root.querySelector('[data-sr-email]');
      var v = el && el.value ? el.value.trim() : '';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
    }

    function openModal(source) {
      overlay.classList.add('is-open');
      document.body.classList.add('sr-spin-modal-open');
      track('spin_viewed', { source: source || 'launcher' });
      // Re-read the saved state on EVERY open (v16): a visitor who spun, closed and reopened in the same page load must
      // see their prize, not the capture form again.
      var current = readState();
      if (current && current.completed) {
        showResult(current, false);
      } else {
        showStep('capture');
      }
    }
    function closeModal() {
      overlay.classList.remove('is-open');
      document.body.classList.remove('sr-spin-modal-open');
      track('spin_closed', {});
      if (window.SRAttentionManager) window.SRAttentionManager.release('spin_auto');
    }

    launcher.addEventListener('click', function () { openModal('launcher'); });
    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });
    root.querySelectorAll('[data-sr-continue-shopping]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    function showStep(name) {
      Object.keys(steps).forEach(function (k) {
        steps[k].classList.toggle('is-active', k === name);
      });
    }

    mobileInput.addEventListener('input', function () { captureErr.textContent = ''; });

    continueBtn.addEventListener('click', function () {
      var digits = validMobile(mobileInput.value);
      if (!digits) {
        captureErr.textContent = window.SR_SPIN_INTL
          ? 'Please enter your full number with country code.'
          : 'Please enter a valid 10-digit mobile number (digits only, no spaces or +91).';
        return;
      }
      if (!consentInput.checked) {
        captureErr.textContent = 'Please agree to receive promotional messages to continue.';
        return;
      }
      track('mobile_submitted', { has_name: !!(nameInput && nameInput.value) });
      track('marketing_consent_given', {
        consent: true,
        consent_source: 'Spin Wheel',
        consent_ts: new Date().toISOString()
      });

      // v22: wait (max RELAY_TIMEOUT_MS) for the server to say what this person may do, BEFORE showing the wheel.
      var continueLabel = continueBtn.textContent;
      continueBtn.disabled = true;
      continueBtn.textContent = 'Checking…';

      relaySubmitLead({
        mobile: digits,
        consent: true,
        name: (nameInput && nameInput.value) || null,
        email: spinEmail(),
        prizeId: null,
        code: null
      }).then(function (resp) {
        continueBtn.disabled = false;
        continueBtn.textContent = continueLabel;

        var el = resp && resp.eligibility;

        // Not accepted (for example a number the server rejects): say why, stay on this step.
        if (resp && resp.status === 'error' && resp.message && !el) {
          captureErr.textContent = resp.message;
          return;
        }

        if (el && el.mode === 'blocked') {
          captureErr.textContent = el.message || 'You have already used your spin recently.';
          track('spin_blocked', {});
          return;
        }

        if (el && el.mode === 'reuse' && el.code) {
          var oldPrize = prizeForPercent(el.percent);
          var repeatState = {
            completed: true,
            repeat: true,
            prizeId: oldPrize ? oldPrize.id : null,
            percent: typeof el.percent === 'number' ? el.percent : null,
            code: el.code,
            mobile: digits,
            consent: true,
            ts: new Date().toISOString()
          };
          writeState(repeatState);
          track('spin_repeat_shown', { prize_id: repeatState.prizeId });
          showResult(repeatState, false);
          return;
        }

        spinCap = (el && el.mode === 'spin' && typeof el.cap_percent === 'number') ? el.cap_percent : null;
        showStep('spin');
        spinCta.disabled = false;
      });
    });

    var spun = false;
    var spinCtaOriginalHtml = spinCta.innerHTML;
    spinCta.addEventListener('click', function () {
      if (spun) {
        // Never swallow the click: if this browser already holds a completed spin, show it.
        var done = readState();
        if (done && done.completed) showResult(done, false);
        return;
      }
      spun = true;
      spinCta.disabled = true;
      spinCta.innerHTML = 'SPINNING<br>&hellip;';
      track('spin_started', {});

      var picked = weightedPick();

      // Fire the CRM relay as soon as the prize is decided (not after the wheel stops) so the minted code is virtually
      // always back before the result screen paints.
      var pickedLocalCode = codeForPrize(picked.prize);
      var pickedMobile = validMobile(mobileInput.value);
      var lead = settleBox(relaySubmitLead({
        mobile: pickedMobile,
        consent: true,
        name: (nameInput && nameInput.value) || null,
        email: spinEmail(),
        prizeId: picked.prize.id,
        code: pickedLocalCode
      }));

      var segAngle = 360 / TOTAL_SEGMENTS;
      var targetSegMid = picked.prize.segIndex * segAngle + segAngle / 2;
      var extraSpins = 5; // full rotations for visual effect
      var finalRotation = extraSpins * 360 + (360 - targetSegMid);
      var SPIN_DURATION_MS = 4200;

      spinWheelTo(wheelSvg, finalRotation, SPIN_DURATION_MS, function () {
        var code = pickedLocalCode;
        var state = {
          completed: true,
          prizeId: picked.prize.id,
          code: code,
          mobile: pickedMobile,
          consent: true,
          ts: new Date().toISOString()
        };
        writeState(state);
        track('spin_completed', { prize_id: picked.prize.id });
        if (code) track('prize_won', { prize_id: picked.prize.id, code: code });
        if (code) track('coupon_generated', { prize_id: picked.prize.id, code: code });
        spinCta.innerHTML = spinCtaOriginalHtml;
        window.setTimeout(function () {
          // Common path: the relay already answered during the spin, so the minted code is adopted BEFORE the screen
          // paints and the visitor never sees the shared fallback code at all.
          if (lead.settled) {
            adoptServerCode(state, lead.value);
            showResult(state, true);
            return;
          }

          // Slow relay: paint the result, hold the code slot rather than showing the shared code, and disable Apply until
          // we know what to apply. RELAY_TIMEOUT_MS guarantees this resolves.
          showResult(state, true);
          couponCode.textContent = 'Generating your code…';
          applyBtn.disabled = true;
          lead.promise.then(function (response) {
            adoptServerCode(state, response);
            couponCode.textContent = state.code || '';
            applyBtn.disabled = false;
          });
        }, 220);
      });
    });

    function findPrize(id) {
      for (var i = 0; i < PRIZES.length; i++) if (PRIZES[i].id === id) return PRIZES[i];
      return null;
    }

    var couponMetaDefaultText = 'Tap Apply Offer to add this discount automatically - no code to type in.';

    function showResult(state, animate) {
      showStep('result');
      if (animate && !prefersReducedMotion()) {
        var resultStepEl = steps.result;
        resultStepEl.classList.remove('sr-result-reveal');
        void resultStepEl.offsetWidth;
        resultStepEl.classList.add('sr-result-reveal');
      }
      var prize = findPrize(state.prizeId);

      // v22: a returning visitor who already holds a usable code (a free-shipping code has no wheel prize, so it is shown without a %).
      if (state.repeat && state.code) {
        resultIcon.textContent = String.fromCodePoint(0x1F389);
        resultTitle.classList.remove('is-empty');
        resultTitle.textContent = prize ? 'Welcome back! You already have ' + prize.value + '% OFF' : 'Welcome back! You already have an offer';
        resultDesc.textContent = 'Here is your active code - one offer per customer.';
        couponCard.style.display = '';
        couponCode.textContent = state.code;
        couponMeta.textContent = couponMetaDefaultText;
        couponMeta.classList.remove('sr-coupon-meta--warning');
        applyBtn.style.display = '';
        applyBtn.disabled = false;
        applyBtn.textContent = applyBtnDefaultLabel;
        return;
      }

      if (!prize) {
        resultTitle.classList.add('is-empty');
        couponCard.style.display = 'none';
        applyBtn.style.display = 'none';
        return;
      }

      resultIcon.textContent = String.fromCodePoint(0x1F389);
      resultTitle.classList.remove('is-empty');
      resultTitle.textContent = 'You won ' + prize.value + '% OFF!';
      resultDesc.textContent = 'Your exclusive offer is ready.';
      couponCard.style.display = '';
      couponCode.textContent = state.code || '';
      couponMeta.textContent = couponMetaDefaultText;
      couponMeta.classList.remove('sr-coupon-meta--warning');
      applyBtn.style.display = '';
      applyBtn.disabled = false;
      applyBtn.textContent = applyBtnDefaultLabel;

      if (animate) spawnConfetti();
    }

    // Synchronous, verifiable cart discount application.
    var applyBtnDefaultLabel = applyBtn.textContent;

    function cartUpdateDiscount(discountValue) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountValue })
      }).then(function (r) { return r.json(); });
    }

    applyBtn.addEventListener('click', function () {
      var state = readState();
      if (!(state && state.code)) return;
      track('coupon_applied', { code: state.code });

      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying...';

      // Clear any existing discount FIRST, then apply the newly won one, so only the code the customer just chose can
      // ever end up active.
      cartUpdateDiscount('')
        .then(function () { return cartUpdateDiscount(state.code); })
        .then(function (cart) {
          var itemCount = (cart && cart.item_count) || 0;

          // Empty cart - nothing to validate the discount against yet. The code is still set on the cart for whenever the
          // shopper adds something (it applies automatically).
          // v20: no second "Continue Shopping" tap. Confirm for a moment, close the popup and go to the collection page.
          if (itemCount === 0) {
            track('coupon_apply_saved_empty_cart', { code: state.code });
            applyBtn.disabled = true;
            applyBtn.textContent = 'Code Saved ✓';
            couponMeta.textContent = 'Your code is saved and will apply automatically when you add items. Taking you to our collection…';
            couponMeta.classList.remove('sr-coupon-meta--warning');
            window.setTimeout(function () {
              closeModal();
              if (!/^\/collections\/all\/?$/.test(window.location.pathname)) window.location.href = '/collections/all';
            }, 1100);
            return;
          }

          var applied = (cart.discount_codes || []).some(function (d) {
            return d.code === state.code && d.applicable;
          });
          track(applied ? 'coupon_apply_confirmed' : 'coupon_apply_blocked', { code: state.code });

          if (!applied) {
            applyBtn.disabled = false;
            applyBtn.textContent = applyBtnDefaultLabel;
            couponMeta.textContent = 'This offer could not be combined with another active discount already on your cart, so it was not applied. Your cart already has the best available discount.';
            couponMeta.classList.add('sr-coupon-meta--warning');
            return;
          }

          applyBtn.disabled = true;
          applyBtn.textContent = 'Applied ✓';
          couponMeta.textContent = 'Applied! This discount is now on your cart and will be used automatically at checkout.';
          couponMeta.classList.remove('sr-coupon-meta--warning');
        })
        .catch(function () {
          applyBtn.disabled = false;
          applyBtn.textContent = applyBtnDefaultLabel;
          couponMeta.textContent = 'Something went wrong applying this offer. Please try again.';
          couponMeta.classList.add('sr-coupon-meta--warning');
        });
    });

    function spawnConfetti() {
      var colors = ['#8B1E3F', '#D4AF37', '#1F5D3A', '#FBF6EE'];
      for (var i = 0; i < 24; i++) {
        var p = document.createElement('span');
        p.className = 'sr-confetti-piece';
        p.style.left = (Math.random() * 100) + '%';
        p.style.background = colors[i % colors.length];
        p.style.animationDelay = (Math.random() * 0.3) + 's';
        confettiHost.appendChild(p);
        (function (el) { window.setTimeout(function () { el.remove(); }, 2000); })(p);
      }
    }

    // Intent-based auto-open (2026-08-18 PageSpeed fix): no fixed timer. Scroll past ~30%, OR (mobile) 3s after the
    // first touch, OR (desktop) exit-intent. Once per session; never when a spin is already completed; guarded by the
    // Customer Assistant / SRAttentionManager collision checks.
    var AUTO_OPEN_TOUCH_DELAY_MS = 3000;
    var AUTO_OPEN_SCROLL_RATIO = 0.3;
    var AUTO_OPEN_RETRY_MS = 3000;   // v19: was 4000
    var AUTO_OPEN_MAX_RETRIES = 15;  // v19: was a single retry; 15 x 3s = ~45s of waiting for the slot
    var AUTO_OPEN_SESSION_KEY = 'sr_spin_auto_shown_v1';
    var ATTENTION_STORE_KEY = 'sr_attention_cooldowns_v1';

    // v19: remove ONLY our own 24h cooldown entry from the attention arbiter's store (see the v19 note at the top).
    function dropOwnAttentionCooldown() {
      try {
        var obj = JSON.parse(window.localStorage.getItem(ATTENTION_STORE_KEY)) || {};
        if (obj.spin_auto) {
          delete obj.spin_auto;
          window.localStorage.setItem(ATTENTION_STORE_KEY, JSON.stringify(obj));
        }
      } catch (e) {}
    }

    function maybeAutoOpen() {
      var alreadyShownThisSession = false;
      try { alreadyShownThisSession = !!window.sessionStorage.getItem(AUTO_OPEN_SESSION_KEY); }
      catch (e) { return; }
      if (alreadyShownThisSession) return;
      if (existing && existing.completed) return;

      function markShown() {
        try { window.sessionStorage.setItem(AUTO_OPEN_SESSION_KEY, '1'); } catch (e) {}
      }

      function attempt(retriesLeft) {
        if (overlay.classList.contains('is-open')) { markShown(); return; }
        // v18: something else (sr-capture-pack.js, for a high-intent arrival) already opened the popup this session.
        try { if (window.sessionStorage.getItem(AUTO_OPEN_SESSION_KEY)) return; } catch (e) {}
        // A saved spin / number that appeared while we were waiting (another tab) - never pop up on someone who has one.
        var nowState = readState();
        if (nowState && nowState.completed) return;
        if (document.body.classList.contains('sr-ca-panel-open')) {
          if (retriesLeft > 0) { window.setTimeout(function () { attempt(retriesLeft - 1); }, AUTO_OPEN_RETRY_MS); }
          else { markShown(); }
          return;
        }
        if (window.SRAttentionManager) {
          dropOwnAttentionCooldown();
          if (!window.SRAttentionManager.request('spin_auto')) {
            if (retriesLeft > 0) { window.setTimeout(function () { attempt(retriesLeft - 1); }, AUTO_OPEN_RETRY_MS); }
            else { markShown(); }
            return;
          }
        }
        markShown();
        openModal('auto');
      }

      var fired = false;
      function fire() {
        if (fired) return;
        fired = true;
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseout', onExit);
        document.removeEventListener('touchstart', onTouch);
        attempt(AUTO_OPEN_MAX_RETRIES);
      }
      function onScroll() {
        var doc = document.documentElement;
        var max = (doc.scrollHeight - window.innerHeight) || 1;
        if ((window.scrollY || doc.scrollTop || 0) / max >= AUTO_OPEN_SCROLL_RATIO) fire();
      }
      function onExit(e) {
        if (!e.relatedTarget && e.clientY <= 10) fire();
      }
      function onTouch() {
        window.setTimeout(fire, AUTO_OPEN_TOUCH_DELAY_MS);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('mouseout', onExit);
      document.addEventListener('touchstart', onTouch, { passive: true, once: true });
    }
    maybeAutoOpen();
  });
})();
